import { useState, useEffect, useRef } from "react";
import { useDialogFocus } from "../../../hooks/useDialogFocus";
import { updateCrmContact } from "../../../services/crmContacts";
import type { UnifiedContactResponse, PatchContactRequest } from "../../../types/crm";
import { SelectPopover } from "../../../components/SelectPopover";
import { ComboboxPopover } from "../../../components/ComboboxPopover";
import { MultiStateSelect } from "../../../components/MultiStateSelect";
import { DIVISIONS } from "../../../constants/divisions";
import {
  PIPELINE_STAGE_LABELS,
  pipelineStageLabel,
  pipelineStageValue,
} from "../../../constants/pipelineStages";
import { RECRUITMENT_STAGES } from "../../../constants/recruitmentStages";
import {
  availablePreferredContactMethodLabels,
  preferredContactMethodLabel,
  preferredContactMethodValue,
} from "../../../constants/preferredContactMethods";
import { CrmTeamTypeahead } from "../../../components/CrmTeamTypeahead";
import type { CrmTeamOption } from "../../../components/CrmTeamTypeahead";
import { POSITION_OPTIONS } from "../../../constants/positions";
import { US_STATES } from "../../../constants/usStates";
import {
  formatPhoneNumber,
  stripPhoneFormat,
  formatHeight,
  parseHeight,
  stripHandlePrefix,
} from "../../../utils/formatters";
import { HandleInput } from "../../../components/HandleInput";
import { ModalCloseButton } from "../../../components/ModalCloseButton";
import "./AddContactModal.css";
import "../../players/playerForm/PlayerForm.css";

interface EditContactModalProps {
  isOpen: boolean;
  contact: UnifiedContactResponse;
  onClose: () => void;
  onSuccess: (updated: UnifiedContactResponse) => void;
}

interface FormErrors {
  [key: string]: string | undefined;
}

export const EditContactModal = ({ isOpen, contact, onClose, onSuccess }: EditContactModalProps) => {
  // Shared fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [xHandle, setXHandle] = useState("");
  const [slackUsername, setSlackUsername] = useState("");

  // Coach-specific
  const [selectedTeam, setSelectedTeam] = useState<CrmTeamOption | null>(null);
  const [preferredContactMethod, setPreferredContactMethod] = useState("");

  // ProspectProfile fields
  const [position, setPosition] = useState("");
  const [bats, setBats] = useState("");
  const [throws_, setThrows] = useState("");
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");
  const [weight, setWeight] = useState("");
  const [gradYear, setGradYear] = useState("");
  const [school, setSchool] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [gpa, setGpa] = useState("");
  const [intendedMajor, setIntendedMajor] = useState("");
  const [preferredLocations, setPreferredLocations] = useState<string[]>([]);
  const [projectedDivisions, setProjectedDivisions] = useState<string[]>([]);
  const [videoLink, setVideoLink] = useState("");
  const [scoutingProfileLink, setScoutingProfileLink] = useState("");
  const [travelTeam, setTravelTeam] = useState("");
  const [travelTeamCoachName, setTravelTeamCoachName] = useState("");
  const [travelTeamCoachPhone, setTravelTeamCoachPhone] = useState("");
  const [pipelineStage, setPipelineStage] = useState("");
  const [recruitmentStage, setRecruitmentStage] = useState("");
  const [endGoal, setEndGoal] = useState("");
  const [notes, setNotes] = useState("");

  const [errors, setErrors] = useState<FormErrors>({});
  const [globalError, setGlobalError] = useState("");
  const [saving, setSaving] = useState(false);

  // Populate form from contact data
  useEffect(() => {
    if (!isOpen || !contact) return;

    const nameParts = contact.displayName.split(" ");
    setFirstName(nameParts[0] || "");
    setLastName(nameParts.slice(1).join(" ") || "");
    setEmail(contact.email || "");
    setPhone(formatPhoneNumber(contact.phone || ""));
    setXHandle(stripHandlePrefix(contact.xHandle));
    setSlackUsername(contact.slackUsername ?? "");

    if (contact.contactType === "ProspectProfile") {
      setPosition(contact.position || "");
      setBats(contact.bats || "");
      setThrows(contact.throws || "");
      const { feet, inches } = parseHeight(contact.height || null);
      setHeightFeet(feet);
      setHeightInches(inches);
      setWeight(contact.weight?.toString() || "");
      setGradYear(contact.gradYear?.toString() || "");
      setSchool(contact.school || "");
      setCity(contact.city || "");
      setState(contact.state || "");
      setGpa(contact.gpa || "");
      setIntendedMajor(contact.intendedMajor || "");
      setPreferredLocations(contact.preferredLocations || []);
      setProjectedDivisions(contact.projectedDivisions || []);
      setVideoLink(contact.videoLink || "");
      setScoutingProfileLink(contact.scoutingProfileLink || "");
      setTravelTeam(contact.travelTeam || "");
      setTravelTeamCoachName(contact.travelTeamCoachName || "");
      setTravelTeamCoachPhone(formatPhoneNumber(contact.travelTeamCoachPhone || ""));
      setPipelineStage(contact.pipelineStage || "");
      setRecruitmentStage(contact.recruitmentStage || "");
      setEndGoal(contact.endGoal || "");
    }

    setNotes(contact.notes || "");

    if (contact.contactType === "Coach") {
      setSelectedTeam(contact.crmTeamId ? { id: contact.crmTeamId, name: contact.crmTeamName || "" } : null);
      setPreferredContactMethod(contact.preferredContactMethod || "");
    }

    setErrors({});
    setGlobalError("");
  }, [isOpen, contact]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Auto-clear preferred contact method if the backing contact field is cleared
  useEffect(() => {
    if (!email.trim() && preferredContactMethod === "Gmail") setPreferredContactMethod("");
    if (!stripPhoneFormat(phone) && preferredContactMethod === "Quo") setPreferredContactMethod("");
    if (!xHandle.trim() && preferredContactMethod === "X") setPreferredContactMethod("");
  }, [email, phone, xHandle, preferredContactMethod]);

  const dialogRef = useRef<HTMLFormElement>(null);

  useDialogFocus({ isOpen, dialogRef, handleEscape: false });

  if (!isOpen) return null;

  const clearError = (field: string) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!firstName.trim()) newErrors.firstName = "Required";
    if (!lastName.trim()) newErrors.lastName = "Required";

    if (contact.contactType === "Coach") {
      if (!email.trim() && !stripPhoneFormat(phone) && !xHandle.trim())
        newErrors.email = "Enter at least one of: email, phone, or X handle";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).filter((k) => newErrors[k]).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setGlobalError("");
    setSaving(true);

    try {
      const nullFields: string[] = [];
      const req: PatchContactRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      };

      // Common fields
      if (email.trim()) req.email = email.trim();
      else nullFields.push("email");
      if (stripPhoneFormat(phone)) req.phone = stripPhoneFormat(phone);
      else nullFields.push("phone");
      if (xHandle.trim()) req.xHandle = xHandle.trim();
      else nullFields.push("xHandle");

      if (contact.contactType === "ProspectProfile") {
        if (slackUsername.trim()) req.slackUsername = slackUsername.trim();
        else nullFields.push("slackUsername");
        if (position) req.position = position;
        else nullFields.push("position");
        if (bats) req.bats = bats;
        else nullFields.push("bats");
        if (throws_) req.throws = throws_;
        else nullFields.push("throws");
        const h = formatHeight(heightFeet, heightInches);
        if (h) req.height = h;
        else nullFields.push("height");
        if (weight) req.weight = parseInt(weight, 10);
        else nullFields.push("weight");
        if (gradYear.trim()) req.gradYear = gradYear.trim();
        else nullFields.push("gradYear");
        if (school.trim()) req.school = school.trim();
        else nullFields.push("school");
        if (city.trim()) req.city = city.trim();
        else nullFields.push("city");
        if (state) req.state = state;
        else nullFields.push("state");
        if (gpa.trim()) req.gpa = gpa.trim();
        else nullFields.push("gpa");
        if (intendedMajor.trim()) req.intendedMajor = intendedMajor.trim();
        else nullFields.push("intendedMajor");
        if (preferredLocations.length > 0) req.preferredLocations = preferredLocations;
        else nullFields.push("preferredLocations");
        if (projectedDivisions.length > 0) req.projectedDivisions = projectedDivisions;
        else nullFields.push("projectedDivisions");
        if (videoLink.trim()) req.videoLink = videoLink.trim();
        else nullFields.push("videoLink");
        if (scoutingProfileLink.trim()) req.scoutingProfileLink = scoutingProfileLink.trim();
        else nullFields.push("scoutingProfileLink");
        if (travelTeam.trim()) req.travelTeam = travelTeam.trim();
        else nullFields.push("travelTeam");
        if (travelTeamCoachName.trim()) req.travelTeamCoachName = travelTeamCoachName.trim();
        else nullFields.push("travelTeamCoachName");
        if (stripPhoneFormat(travelTeamCoachPhone)) req.travelTeamCoachPhone = stripPhoneFormat(travelTeamCoachPhone);
        else nullFields.push("travelTeamCoachPhone");
        // Pipeline stage is non-nullable on the API side — only send it when set, never clear it.
        if (pipelineStage) req.pipelineStage = pipelineStage;
        if (recruitmentStage) req.recruitmentStage = recruitmentStage;
        else nullFields.push("recruitmentStage");
        if (endGoal.trim()) req.endGoal = endGoal.trim();
        else nullFields.push("endGoal");
      }

      // Notes (all contact types)
      if (notes.trim()) req.notes = notes.trim();
      else nullFields.push("notes");

      if (contact.contactType === "Coach") {
        if (selectedTeam) req.crmTeamId = selectedTeam.id;
        if (preferredContactMethod) req.preferredContactMethod = preferredContactMethod;
        else nullFields.push("preferredContactMethod");
      }

      if (nullFields.length > 0) req.nullFields = nullFields;

      const updated = await updateCrmContact(contact.contactType, contact.id, req);
      onSuccess(updated);
    } catch {
      setGlobalError("Failed to update contact. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    opts?: { type?: string; placeholder?: string; required?: boolean; errorKey?: string }
  ) => {
    const errKey = opts?.errorKey || label.toLowerCase().replace(/[^a-z]/g, "");
    return (
      <div className="add-contact-modal__field">
        <label className="add-contact-modal__label">
          {label}{opts?.required ? " *" : ""}
        </label>
        <input
          className={`add-contact-modal__input${errors[errKey] ? " add-contact-modal__input--error" : ""}`}
          type={opts?.type || "text"}
          value={value}
          onChange={(e) => { onChange(e.target.value); clearError(errKey); }}
          placeholder={opts?.placeholder}
        />
        {errors[errKey] && <span className="add-contact-modal__field-error">{errors[errKey]}</span>}
      </div>
    );
  };

  const typeLabel = contact.contactType === "ProspectProfile" ? "Player" : contact.contactType;

  return (
    <div className="add-contact-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form ref={dialogRef} tabIndex={-1} className={`add-contact-modal${contact.contactType === "ProspectProfile" ? " add-contact-modal--wide" : ""}`} role="dialog" aria-modal="true" aria-label={`Edit ${typeLabel}`} onSubmit={handleSubmit}>
        <div className="add-contact-modal__header">
          <h2 className="add-contact-modal__title">Edit {typeLabel}</h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="add-contact-modal__body">
          {/* PERSONAL INFORMATION */}
          {contact.contactType === "ProspectProfile" && (
            <div className="add-contact-modal__section-header">Personal Information</div>
          )}

          <div className="add-contact-modal__row">
            {renderField("First Name", firstName, setFirstName, { required: true, errorKey: "firstName" })}
            {renderField("Last Name", lastName, setLastName, { required: true, errorKey: "lastName" })}
          </div>

          <div className="add-contact-modal__row">
            {renderField("Email", email, setEmail, { type: "email", errorKey: "email" })}
            <div className="add-contact-modal__field">
              <label htmlFor="edit-contact-modal-phone-0" className="add-contact-modal__label">Phone</label>
              <input id="edit-contact-modal-phone-0"
                className="add-contact-modal__input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          {contact.contactType === "ProspectProfile" && (
            <>
              <div className="add-contact-modal__row">
                <HandleInput
                  label="X Handle"
                  value={xHandle}
                  onChange={setXHandle}
                  placeholder="handle"
                  variant="dashboard"
                  wrapperClassName="add-contact-modal__field"
                />
                <div className="add-contact-modal__field">
                  <label htmlFor="edit-contact-modal-slack-username-1" className="add-contact-modal__label">Slack Username</label>
                  <input id="edit-contact-modal-slack-username-1"
                    className="add-contact-modal__input"
                    type="text"
                    value={slackUsername}
                    onChange={(e) => setSlackUsername(e.target.value)}
                    placeholder="username or email"
                  />
                </div>
              </div>

              {/* LINKS */}
              <div className="add-contact-modal__section-header">Links</div>
              <div className="add-contact-modal__row">
                {renderField("Video Link", videoLink, setVideoLink, { placeholder: "https://...", errorKey: "videoLink" })}
                {renderField("Scouting Profile Link", scoutingProfileLink, setScoutingProfileLink, { placeholder: "https://...", errorKey: "scoutingProfileLink" })}
              </div>

              {/* PLAYER DETAILS */}
              <div className="add-contact-modal__section-header">Player Details</div>
              <div className="add-contact-modal__field">
                <label htmlFor="edit-contact-modal-main-position-0" className="add-contact-modal__label">Main Position</label>
                <SelectPopover id="edit-contact-modal-main-position-0"
                  options={POSITION_OPTIONS}
                  value={position}
                  onChange={setPosition}
                  placeholder="Select position..."
                  fullWidth
                />
              </div>

              <div className="add-contact-modal__row">
                <div className="add-contact-modal__field">
                  <label htmlFor="edit-contact-modal-bats-1" className="add-contact-modal__label">Bats</label>
                  <SelectPopover id="edit-contact-modal-bats-1" options={["L", "R"]} value={bats} onChange={setBats} placeholder="L / R" fullWidth />
                </div>
                <div className="add-contact-modal__field">
                  <label htmlFor="edit-contact-modal-throws-2" className="add-contact-modal__label">Throws</label>
                  <SelectPopover id="edit-contact-modal-throws-2" options={["L", "R"]} value={throws_} onChange={setThrows} placeholder="L / R" fullWidth />
                </div>
              </div>

              <div className="add-contact-modal__row add-contact-modal__row--three">
                {renderField("Height (ft)", heightFeet, setHeightFeet, { type: "number", placeholder: "6", errorKey: "heightFeet" })}
                {renderField("Height (in)", heightInches, setHeightInches, { type: "number", placeholder: "1", errorKey: "heightInches" })}
                {renderField("Weight (lbs)", weight, setWeight, { type: "number", placeholder: "185", errorKey: "weight" })}
              </div>

              {/* ACADEMIC & LOCATION */}
              <div className="add-contact-modal__section-header">Academic & Location</div>
              <div className="add-contact-modal__row">
                {renderField("School", school, setSchool, { errorKey: "school" })}
                {renderField("Class", gradYear, setGradYear, { type: "number", placeholder: "2026", errorKey: "gradYear" })}
              </div>

              <div className="add-contact-modal__row">
                <div className="add-contact-modal__field">
                  <label htmlFor="edit-contact-modal-city-2" className="add-contact-modal__label">City</label>
                  <input id="edit-contact-modal-city-2" className="add-contact-modal__input" type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
                </div>
                <div className="add-contact-modal__field">
                  <label htmlFor="edit-contact-modal-state-3" className="add-contact-modal__label">State</label>
                  <ComboboxPopover id="edit-contact-modal-state-3" options={US_STATES} value={state} onChange={setState} placeholder="State" transform={(v) => v.toUpperCase().slice(0, 2)} fullWidth />
                </div>
              </div>

              <div className="add-contact-modal__row">
                {renderField("GPA", gpa, setGpa, { placeholder: "3.5", errorKey: "gpa" })}
                {renderField("Intended Major", intendedMajor, setIntendedMajor, { errorKey: "intendedMajor" })}
              </div>

              <MultiStateSelect
                label="Preferred Location"
                value={preferredLocations}
                onChange={setPreferredLocations}
                max={3}
              />

              {/* TRAVEL TEAM */}
              <div className="add-contact-modal__section-header">Travel Team</div>
              {renderField("Travel Team Name", travelTeam, setTravelTeam, { errorKey: "travelTeam" })}
              <div className="add-contact-modal__row">
                {renderField("Coach Name", travelTeamCoachName, setTravelTeamCoachName, { errorKey: "travelTeamCoachName" })}
                <div className="add-contact-modal__field">
                  <label htmlFor="edit-contact-modal-coach-phone-3" className="add-contact-modal__label">Coach Phone</label>
                  <input id="edit-contact-modal-coach-phone-3"
                    className="add-contact-modal__input"
                    type="tel"
                    value={travelTeamCoachPhone}
                    onChange={(e) => setTravelTeamCoachPhone(formatPhoneNumber(e.target.value))}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* RECRUITING */}
              <div className="add-contact-modal__section-header">Recruiting</div>
              <div className="add-contact-modal__row add-contact-modal__row--three">
                <div className="add-contact-modal__field">
                  <label className="add-contact-modal__label" htmlFor="edit-contact-pipeline-stage">
                    Pipeline Stage
                  </label>
                  <SelectPopover
                    id="edit-contact-pipeline-stage"
                    options={PIPELINE_STAGE_LABELS}
                    value={pipelineStageLabel(pipelineStage)}
                    onChange={(label) => setPipelineStage(pipelineStageValue(label) ?? "")}
                    placeholder="Select stage..."
                    fullWidth
                  />
                </div>
                <div className="add-contact-modal__field">
                  <label htmlFor="edit-contact-modal-recruitment-stage-4" className="add-contact-modal__label">Recruitment Stage</label>
                  <SelectPopover
                    id="edit-contact-modal-recruitment-stage-4"
                    options={RECRUITMENT_STAGES}
                    value={recruitmentStage}
                    onChange={setRecruitmentStage}
                    placeholder="Select stage..."
                    fullWidth
                  />
                </div>
                {renderField("End Goal", endGoal, setEndGoal, { placeholder: "e.g., Play D1 baseball", errorKey: "endGoal" })}
              </div>
              <div className="add-contact-modal__field">
                <span className="add-contact-modal__label" id="edit-contact-modal-projected-divisions-up-to-3-0">Projected Divisions (up to 3)</span>
                <div className="submission-state-chips" role="group" aria-labelledby="edit-contact-modal-projected-divisions-up-to-3-0">
                  {DIVISIONS.map((d) => {
                    const selected = projectedDivisions.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        className={`submission-state-chip${selected ? " submission-state-chip--selected" : ""}`}
                        onClick={() => {
                          setProjectedDivisions((prev) => {
                            if (prev.includes(d)) return prev.filter((x) => x !== d);
                            if (prev.length >= 3) return prev;
                            return [...prev, d];
                          });
                        }}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Coach edit */}
          {contact.contactType === "Coach" && (
            <>
              <div className="add-contact-modal__row">
                <HandleInput
                  label="X Handle"
                  value={xHandle}
                  onChange={(v) => {
                    setXHandle(v);
                    if (!v && preferredContactMethod === "X") setPreferredContactMethod("");
                  }}
                  placeholder="handle"
                  variant="dashboard"
                  wrapperClassName="add-contact-modal__field"
                />
                <div className="add-contact-modal__field">
                  <label htmlFor="edit-contact-modal-preferred-contact-5" className="add-contact-modal__label">Preferred Contact</label>
                  <SelectPopover
                    id="edit-contact-modal-preferred-contact-5"
                    options={availablePreferredContactMethodLabels(email, phone, xHandle)}
                    value={preferredContactMethodLabel(preferredContactMethod)}
                    onChange={(label) => setPreferredContactMethod(preferredContactMethodValue(label) ?? "")}
                    placeholder="Select..."
                    fullWidth
                  />
                </div>
              </div>
              <div className="add-contact-modal__field add-contact-modal__field--typeahead">
                <label htmlFor="edit-contact-modal-team-0" className="add-contact-modal__label">Team</label>
                <CrmTeamTypeahead inputId="edit-contact-modal-team-0"
                  value={selectedTeam}
                  onSelect={setSelectedTeam}
                  placeholder="Search teams..."
                />
              </div>
            </>
          )}

          {/* Parent edit */}
          {contact.contactType === "Parent" && (
            <div className="add-contact-modal__row">
              <HandleInput
                label="X Handle"
                value={xHandle}
                onChange={setXHandle}
                placeholder="handle"
                variant="dashboard"
                wrapperClassName="add-contact-modal__field"
              />
              <div className="add-contact-modal__field">
                <label htmlFor="edit-contact-modal-slack-username-6" className="add-contact-modal__label">Slack Username</label>
                <input id="edit-contact-modal-slack-username-6"
                  className="add-contact-modal__input"
                  type="text"
                  value={slackUsername}
                  onChange={(e) => setSlackUsername(e.target.value)}
                  placeholder="username or email"
                />
              </div>
            </div>
          )}

          {/* Notes (all contact types) */}
          {contact.contactType === "ProspectProfile" && (
            <div className="add-contact-modal__section-header">Notes</div>
          )}
          <div className="add-contact-modal__field">
            {contact.contactType !== "ProspectProfile" && (
              <label className="add-contact-modal__label">Notes</label>
            )}
            <textarea
              className="add-contact-modal__input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add notes..."
            />
          </div>

          {globalError && <div className="add-contact-modal__error">{globalError}</div>}
        </div>

        <div className="add-contact-modal__footer">
          <button type="button" className="add-contact-modal__cancel" onClick={onClose}>Cancel</button>
          <button type="submit" className="add-contact-modal__save" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};
